import {Button, Colors, Menu, MenuItem} from "@blueprintjs/core";
import {
    ItemListPredicate,
    ItemListRenderer,
    ItemListRendererProps,
    ItemRenderer,
    ItemRendererProps,
    Select,
} from "@blueprintjs/select";
import {ClassNames} from "@emotion/react";
import styled from "@emotion/styled";
import * as React from "react";

import {TemplateOutput} from "../../../api/values/database-list-output";

type LooseTemplateItem = TemplateOutput | null;

function createKey(item: LooseTemplateItem) {
    return item ? item.id.toString() : "";
}

const TemplateSelect = Select.ofType<LooseTemplateItem>();

function normalizeJP(s: string) {
    if (!s) return "";
    const nk = s.normalize("NFKC").toLowerCase();
    // カタカナ → ひらがな
    return nk.replace(/[\u30A1-\u30F6]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0x60));
}

function includesNormalized(haystack: string, needle: string) {
    return normalizeJP(haystack).includes(normalizeJP(needle));
}

// タイトルだけを対象に検索
function createItemListPredicate(emptyOption?: boolean): ItemListPredicate<LooseTemplateItem> {
    return (query, items) => {
        const arr: LooseTemplateItem[] = [];
        const q = normalizeJP((query ?? "").trim());

        if (emptyOption && !q) arr.push(null);
        if (!q) return arr.concat(items); // 未入力なら全件

        return arr.concat(items.filter((item) => item && includesNormalized(item.title, q)));
    };
}

const TemplateSelectorListMenu = styled(Menu)`
    /* 幅はCSSで固定、縦は props からの style で受ける */
    max-width: 330px;
    overflow: auto;
`;

function createItemListRenderer(
    menuMaxHeight: number | string | undefined,
    menuMaxWidth: number | string | undefined,
): ItemListRenderer<LooseTemplateItem> {
    const ItemListRendererComponent = (props: ItemListRendererProps<LooseTemplateItem>) => {
        const elements: React.ReactNode[] = [];
        let index = 0;
        for (const item of props.filteredItems) {
            elements.push(props.renderItem(item, index++));
        }
        if (!elements.length) {
            elements.push(renderNonIdeal());
        }
        return (
            <TemplateSelectorListMenu
                ulRef={props.itemsParentRef}
                style={{
                    maxHeight: menuMaxHeight ?? 320, // ★ 多い時はここでスクロール
                    maxWidth: menuMaxWidth ?? 330,
                }}
            >
                {elements}
            </TemplateSelectorListMenu>
        );
    };
    ItemListRendererComponent.displayName = "ItemListRendererComponent";
    return ItemListRendererComponent;
}

function createItemRenderer(placeholder?: string): ItemRenderer<LooseTemplateItem> {
    return (item, props) => (item ? renderItem(item, props) : renderEmptyItem(props, placeholder));
}

function renderEmptyItem(props: ItemRendererProps, placeholder?: string) {
    return (
        <MenuItem
            active={props.modifiers.active}
            key={createKey(null)}
            text={<span style={{color: Colors.GRAY3}}>{placeholder || "未選択"}</span>}
            onClick={props.handleClick}
        />
    );
}

function renderItem(item: TemplateOutput, props: ItemRendererProps) {
    return (
        <MenuItem
            active={props.modifiers.active}
            key={createKey(item)}
            text={renderItemContent(item)}
            onClick={props.handleClick}
        />
    );
}

function renderNonIdeal() {
    return <MenuItem disabled={true} key={createKey(null)} text={"No results."} />;
}

const TemplateSpan = styled.span``;

const TemplateNoteSpan = styled.span`
    margin-left: 0.2em;
    color: ${Colors.GRAY3};
`;

function renderItemContent(item: LooseTemplateItem) {
    return item ? (
        <TemplateSpan>
            {item.title}
            <br />
            <TemplateNoteSpan>({item.note || "説明文なし"})</TemplateNoteSpan>
        </TemplateSpan>
    ) : (
        <span style={{color: Colors.GRAY3}}>未選択</span>
    );
}

interface ITemplateSelectorProps {
    value: string | null;
    emptyOption?: boolean;
    placeholder?: string;
    onChange: (item: LooseTemplateItem) => void;
    availableTemplates: TemplateOutput[];
    disabled?: boolean;
    menuMaxHeight?: number | string;
    menuMaxWidth?: number | string;
}

export const TemplateSelector: React.FC<ITemplateSelectorProps> = (props) => {
    const handleChange = (item: LooseTemplateItem) => {
        props.onChange(item);
    };

    const items = props.availableTemplates;
    const selectedItem = items.find((x) => x.id.toString() === props.value) || null;

    const itemListRenderer = React.useMemo(
        () => createItemListRenderer(props.menuMaxHeight, props.menuMaxWidth),
        [props.menuMaxHeight, props.menuMaxWidth],
    );

    return (
        <ClassNames>
            {({css}) => (
                <TemplateSelectWithChildren
                    items={items}
                    itemListPredicate={createItemListPredicate(props.emptyOption)}
                    itemRenderer={createItemRenderer(props.placeholder)}
                    itemListRenderer={itemListRenderer}
                    onItemSelect={handleChange}
                    activeItem={selectedItem}
                    disabled={props.disabled}
                    popoverProps={{
                        captureDismiss: true, // 親Popoverを閉じさせない
                        modifiers: {arrow: {enabled: false}},
                        popoverClassName: css`
                            padding: 5px;
                            background-color: white;
                        `,
                    }}
                    resetOnClose={true}
                    resetOnSelect={true}
                    resetOnQuery={false}
                >
                    <Button rightIcon="caret-down" disabled={props.disabled}>
                        {renderItemContent(selectedItem)}
                    </Button>
                </TemplateSelectWithChildren>
            )}
        </ClassNames>
    );
};

interface TemplateSelectWithChildrenProps extends React.ComponentProps<typeof TemplateSelect> {
    children: React.ReactNode;
}

const TemplateSelectWithChildren: React.FC<TemplateSelectWithChildrenProps> = (props) => {
    return <TemplateSelect {...props}>{props.children}</TemplateSelect>;
};
